import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { toast } from 'react-toastify';
import {
  ADMIN_MATERIAL_SKU_CONFIGURATION_URL,
  ADMIN_NUMBERING_URL,
  ADMIN_PERMISSIONS_URL,
  ADMIN_REGIONS_URL,
  ADMIN_ROLES_URL,
  ADMIN_SYSTEM_CONFIGURATION_URL,
  ADMIN_USER_PROFILES_URL,
  MATERIAL_SKU_CONFIGURATION_URL
} from '../constants';
import type {
  RegionDeliveryAddressDto,
  RegionFieldDto,
  RegionMasterDto,
  UserProfileListItem,
  UserRole
} from '../domain/types';
import { wellRegionLabel } from '../domain/wellRegions';
import { DEFAULT_MATERIAL_SKU_PATTERNS, mergeMaterialSkuPatterns } from '../domain/types';

const ROLE_OPTIONS: UserRole[] = [
  'Requestor',
  'RegionalManager',
  'ProcurementManager',
  'ProcurementSpecialist',
  'OperationsManager',
  'Administrator',
  'ITSupport',
  'ContractAdmin',
  'DivisionOrder',
  'Controller'
];

interface ActionEntitlementRow {
  id: string;
  resourceArea: string;
  action: string;
}

interface ApplicationRoleRow {
  id: string;
  name: string;
  displayName: string;
  isSystem: boolean;
  permissions: string[];
}

function permissionCode(area: string, action: string): string {
  return `${area}:${action}`.toLowerCase();
}

const NUMBERING_SCOPE_OPTIONS = [
  'Global',
  'PerCalendarYear',
  'PerYearMonth',
  'PerRegionAndYear'
] as const;

function sanitizeScopePart(s: string, maxLen: number): string {
  let r = '';
  for (const c of s.trim().toUpperCase()) {
    if (/[A-Z0-9_-]/.test(c)) r += c;
    if (r.length >= maxLen) break;
  }
  return r || 'X';
}

function resolveSeqWidth(pattern: string, defaultW: number): number {
  const m = pattern.match(/\{seq:(\d+)\}/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Math.min(8, Math.max(1, Number.isNaN(n) ? defaultW : n));
  }
  if (/\{seq\}/i.test(pattern)) return Math.min(8, Math.max(1, defaultW));
  return Math.min(8, Math.max(1, defaultW));
}

/** Mirrors server preview sample: region SOK, demo well, current UTC date, sequence 42. */
function previewNumberingPattern(pattern: string, businessCode: string, defaultW: number, seq = 42): string {
  const at = new Date();
  const yyyy = String(at.getUTCFullYear());
  const yy = yyyy.slice(-2);
  const MM = String(at.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(at.getUTCDate()).padStart(2, '0');
  const bc = sanitizeScopePart(businessCode || 'PFLO', 12);
  const width = resolveSeqWidth(pattern, defaultW);
  const seqStr = String(seq).padStart(width, '0');
  return pattern.replace(/\{([^}]+)\}/g, (_, raw: string) => {
    const inner = raw.trim();
    if (/^seq:/i.test(inner) || /^seq$/i.test(inner)) return seqStr;
    if (/^yyyy$/i.test(inner)) return yyyy;
    if (/^yy$/i.test(inner)) return yy;
    if (/^MM$/i.test(inner)) return MM;
    if (/^dd$/i.test(inner)) return dd;
    if (/^RegionCode$/i.test(inner)) return 'SOK';
    if (/^WellId$/i.test(inner)) return 'WELL-DEMO-01';
    if (/^WellNameSlug$/i.test(inner)) return 'DEMOWELL';
    if (/^Business(Code)?$/i.test(inner)) return bc;
    return `{${inner}}`;
  });
}

interface AdminPageProps {
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

export function AdminPage({ authFetch }: AdminPageProps): ReactElement {
  const [tab, setTab] = useState<'users' | 'roles' | 'regions' | 'numbering' | 'materialSku' | 'system'>('users');
  const [users, setUsers] = useState<UserProfileListItem[]>([]);
  const [appRoles, setAppRoles] = useState<ApplicationRoleRow[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<ActionEntitlementRow[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [roleDisplayName, setRoleDisplayName] = useState('');
  const [rolePermissionSet, setRolePermissionSet] = useState<string[]>([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDisplayName, setNewRoleDisplayName] = useState('');
  const [regions, setRegions] = useState<RegionMasterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [numberingForm, setNumberingForm] = useState({
    requestIdPattern: 'PR-{yyyy}-{seq:6}',
    purchaseOrderPattern: 'PO-{seq:6}',
    businessCode: 'PFLO',
    defaultSequenceWidth: 4,
    maxFormattedLength: 64,
    requestSequenceScope: 'Global',
    purchaseOrderSequenceScope: 'Global'
  });

  const [systemForm, setSystemForm] = useState({
    emailNotificationsEnabled: false,
    emailNotificationFrontendBaseUrl: '',
    vendorQuoteLinkExpiryDays: 5,
    sourcingEnabled: true,
    materialTransfersEnabled: true
  });

  const [materialSkuForm, setMaterialSkuForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(DEFAULT_MATERIAL_SKU_PATTERNS) as [string, string][]
    )
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, rr, nr, sy, msku, rolesRes, permsRes] = await Promise.all([
        authFetch(ADMIN_USER_PROFILES_URL),
        authFetch(ADMIN_REGIONS_URL),
        authFetch(ADMIN_NUMBERING_URL),
        authFetch(ADMIN_SYSTEM_CONFIGURATION_URL),
        authFetch(MATERIAL_SKU_CONFIGURATION_URL),
        authFetch(ADMIN_ROLES_URL),
        authFetch(ADMIN_PERMISSIONS_URL)
      ]);
      if (ur.ok) {
        setUsers((await ur.json()) as UserProfileListItem[]);
      } else {
        toast.error('Could not load user profiles.');
      }
      if (rolesRes.ok) {
        const rows = (await rolesRes.json()) as ApplicationRoleRow[];
        setAppRoles(rows);
      }
      if (permsRes.ok) {
        setPermissionCatalog((await permsRes.json()) as ActionEntitlementRow[]);
      }
      if (rr.ok) {
        const rawRows = (await rr.json()) as Record<string, unknown>[];
        setRegions(
          rawRows.map((row) => {
            const fr = row.fields;
            const fields: RegionFieldDto[] = Array.isArray(fr)
              ? (fr as Record<string, unknown>[]).map((f) => ({
                  id: String(f.id ?? ''),
                  name: String(f.name ?? ''),
                  county: String(f.county ?? ''),
                  state: String(f.state ?? ''),
                  closestCity: String(f.closestCity ?? ''),
                  milesFromCity:
                    f.milesFromCity === undefined || f.milesFromCity === null
                      ? undefined
                      : Number(f.milesFromCity)
                }))
              : [];
            const dar = row.deliveryAddresses;
            const deliveryAddresses: RegionDeliveryAddressDto[] = Array.isArray(dar)
              ? (dar as Record<string, unknown>[]).map((d) => {
                  const c = String(d.category ?? '').toLowerCase();
                  return {
                    id: String(d.id ?? ''),
                    label: String(d.label ?? ''),
                    address: String(d.address ?? ''),
                    category: c === 'wellpad' || c === 'well_pad' ? 'WellPad' : 'Yard'
                  };
                })
              : [];
            return {
              id: String(row.id ?? ''),
              code: String(row.code ?? ''),
              name: String(row.name ?? ''),
              isActive: Boolean(row.isActive),
              sortOrder: Number(row.sortOrder ?? 0),
              fields,
              deliveryAddresses
            };
          })
        );
      } else {
        toast.error('Could not load regions.');
      }
      if (nr.ok) {
        const row = (await nr.json()) as Record<string, unknown>;
        setNumberingForm({
          requestIdPattern: String(row.requestIdPattern ?? ''),
          purchaseOrderPattern: String(row.purchaseOrderPattern ?? ''),
          businessCode: String(row.businessCode ?? ''),
          defaultSequenceWidth: Number(row.defaultSequenceWidth ?? 4),
          maxFormattedLength: Number(row.maxFormattedLength ?? 64),
          requestSequenceScope: String(row.requestSequenceScope ?? 'Global'),
          purchaseOrderSequenceScope: String(row.purchaseOrderSequenceScope ?? 'Global')
        });
      } else if (nr.status !== 401 && nr.status !== 403) {
        toast.error('Could not load transaction configuration.');
      }
      if (sy.ok) {
        const sc = (await sy.json()) as Record<string, unknown>;
        const daysRaw = sc.vendorQuoteLinkExpiryDays;
        const days =
          typeof daysRaw === 'number' && Number.isFinite(daysRaw) && daysRaw > 0 ? Math.floor(daysRaw) : 5;
        setSystemForm({
          emailNotificationsEnabled: Boolean(sc.emailNotificationsEnabled),
          emailNotificationFrontendBaseUrl: String(sc.emailNotificationFrontendBaseUrl ?? ''),
          vendorQuoteLinkExpiryDays: days,
          sourcingEnabled:
            typeof sc.sourcingEnabled === 'boolean' ? sc.sourcingEnabled : true,
          materialTransfersEnabled:
            typeof sc.materialTransfersEnabled === 'boolean' ? sc.materialTransfersEnabled : true
        });
      } else if (sy.status !== 401 && sy.status !== 403) {
        toast.error('Could not load system configuration.');
      }
      if (msku.ok) {
        const b = (await msku.json()) as { patternsByKind?: Record<string, string> };
        setMaterialSkuForm({ ...mergeMaterialSkuPatterns(b.patternsByKind) });
      } else if (msku.status !== 401 && msku.status !== 403) {
        toast.error('Could not load material SKU configuration.');
      }
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

    const [userForm, setUserForm] = useState({
    userId: '',
    displayName: '',
    email: '',
    role: 'Requestor' as UserRole,
    isActive: true,
    allRegions: true,
    selectedRegionCodes: [] as string[],
    applicationRoles: [] as string[]
  });

  const emptyRegionFieldRow = (): RegionFieldDto => ({
    id: '',
    name: '',
    county: '',
    state: '',
    closestCity: '',
    milesFromCity: undefined
  });

  const emptyRegionDeliveryRow = (): RegionDeliveryAddressDto => ({
    id: '',
    label: '',
    address: '',
    category: 'Yard'
  });

  const [regionForm, setRegionForm] = useState({
    id: '' as string | undefined,
    code: '',
    name: '',
    isActive: true,
    sortOrder: 0,
    fields: [] as RegionFieldDto[],
    deliveryAddresses: [] as RegionDeliveryAddressDto[]
  });

  async function saveUser(e: FormEvent): Promise<void> {
    e.preventDefault();
    const uid = userForm.userId.trim();
    if (!uid) {
      toast.error('User id is required.');
      return;
    }
    const codes = userForm.allRegions ? [] : [...userForm.selectedRegionCodes];
    const response = await authFetch(`${ADMIN_USER_PROFILES_URL}/${encodeURIComponent(uid)}`, {
      method: 'PUT',
      body: JSON.stringify({
        displayName: userForm.displayName.trim(),
        role: userForm.role,
        isActive: userForm.isActive,
        allRegions: userForm.allRegions,
        assignedRegionCodes: codes,
        email: userForm.email.trim() || ''
      })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not save user profile.');
      return;
    }
    const roleNames =
      userForm.applicationRoles.length > 0 ? userForm.applicationRoles : [userForm.role];
    const rolesRes = await authFetch(`${ADMIN_USER_PROFILES_URL}/${encodeURIComponent(uid)}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roleNames })
    });
    if (!rolesRes.ok) {
      const t = await rolesRes.text();
      toast.error(t || 'Profile saved, but application roles could not be updated.');
      return;
    }
    toast.success('User profile saved.');
    setUserForm({
      userId: '',
      displayName: '',
      email: '',
      role: 'Requestor',
      isActive: true,
      allRegions: true,
      selectedRegionCodes: [],
      applicationRoles: []
    });
    void loadAll();
  }

  async function saveNumbering(e: FormEvent): Promise<void> {
    e.preventDefault();
    const response = await authFetch(ADMIN_NUMBERING_URL, {
      method: 'PUT',
      body: JSON.stringify({
        requestIdPattern: numberingForm.requestIdPattern.trim(),
        purchaseOrderPattern: numberingForm.purchaseOrderPattern.trim(),
        businessCode: numberingForm.businessCode.trim(),
        defaultSequenceWidth: numberingForm.defaultSequenceWidth,
        maxFormattedLength: numberingForm.maxFormattedLength,
        requestSequenceScope: numberingForm.requestSequenceScope,
        purchaseOrderSequenceScope: numberingForm.purchaseOrderSequenceScope
      })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not save transaction configuration.');
      return;
    }
    toast.success('Transaction configuration saved.');
    void loadAll();
  }

  async function saveMaterialSku(e: FormEvent): Promise<void> {
    e.preventDefault();
    const response = await authFetch(ADMIN_MATERIAL_SKU_CONFIGURATION_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patternsByKind: materialSkuForm })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not save material SKU configuration.');
      return;
    }
    toast.success('Material SKU configuration saved.');
    void loadAll();
  }

  async function saveSystemConfiguration(e: FormEvent): Promise<void> {
    e.preventDefault();
    const response = await authFetch(ADMIN_SYSTEM_CONFIGURATION_URL, {
      method: 'PUT',
      body: JSON.stringify({
        emailNotificationsEnabled: systemForm.emailNotificationsEnabled,
        emailNotificationFrontendBaseUrl: systemForm.emailNotificationFrontendBaseUrl.trim() || null,
        vendorQuoteLinkExpiryDays: systemForm.vendorQuoteLinkExpiryDays,
        sourcingEnabled: systemForm.sourcingEnabled,
        materialTransfersEnabled: systemForm.materialTransfersEnabled
      })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not save system configuration.');
      return;
    }
    toast.success('System configuration saved.');
    void loadAll();
  }

  async function saveRegion(e: FormEvent): Promise<void> {
    e.preventDefault();
    const fieldsPayload = regionForm.fields
      .filter((f) => f.name.trim())
      .map((f) => ({
        id: f.id.trim() || '',
        name: f.name.trim(),
        county: f.county.trim(),
        state: f.state.trim(),
        closestCity: f.closestCity.trim(),
        milesFromCity:
          f.milesFromCity === undefined || f.milesFromCity === null || Number.isNaN(f.milesFromCity)
            ? null
            : f.milesFromCity
      }));
    const deliveryPayload = regionForm.deliveryAddresses
      .filter((d) => d.label.trim() && d.address.trim())
      .map((d) => ({
        id: d.id.trim() || '',
        label: d.label.trim(),
        address: d.address.trim(),
        category: d.category === 'WellPad' ? 'WellPad' : 'Yard'
      }));
    const response = await authFetch(ADMIN_REGIONS_URL, {
      method: 'PUT',
      body: JSON.stringify({
        id: regionForm.id || null,
        code: regionForm.code.trim(),
        name: regionForm.name.trim(),
        isActive: regionForm.isActive,
        sortOrder: regionForm.sortOrder,
        fields: fieldsPayload,
        deliveryAddresses: deliveryPayload
      })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not save region.');
      return;
    }
    toast.success('Region saved.');
    setRegionForm({
      id: undefined,
      code: '',
      name: '',
      isActive: true,
      sortOrder: 0,
      fields: [],
      deliveryAddresses: []
    });
    void loadAll();
  }

  async function editUser(row: UserProfileListItem): Promise<void> {
    let applicationRoles: string[] = [row.role];
    const rr = await authFetch(`${ADMIN_USER_PROFILES_URL}/${encodeURIComponent(row.userId)}/roles`);
    if (rr.ok) {
      const names = (await rr.json()) as string[];
      if (names.length > 0) {
        applicationRoles = names;
      }
    }
    setUserForm({
      userId: row.userId,
      displayName: row.displayName,
      email: row.email?.trim() ?? '',
      role: row.role as UserRole,
      isActive: row.isActive,
      allRegions: row.allRegions,
      selectedRegionCodes: [...row.assignedRegionCodes],
      applicationRoles
    });
    setTab('users');
  }

  function editRegion(r: RegionMasterDto): void {
    const fields = r.fields?.length ? [...r.fields] : [emptyRegionFieldRow()];
    const deliveryAddresses = r.deliveryAddresses?.length
      ? [...r.deliveryAddresses]
      : [emptyRegionDeliveryRow()];
    setRegionForm({
      id: r.id || undefined,
      code: r.code,
      name: r.name,
      isActive: r.isActive,
      sortOrder: r.sortOrder,
      fields,
      deliveryAddresses
    });
    setTab('regions');
  }

  function selectRole(row: ApplicationRoleRow): void {
    setSelectedRoleId(row.id);
    setRoleDisplayName(row.displayName);
    setRolePermissionSet([...row.permissions]);
  }

  async function saveSelectedRole(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!selectedRoleId) {
      toast.error('Select a role first.');
      return;
    }
    const response = await authFetch(`${ADMIN_ROLES_URL}/${encodeURIComponent(selectedRoleId)}`, {
      method: 'PUT',
      body: JSON.stringify({ displayName: roleDisplayName.trim(), permissions: rolePermissionSet })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not save role permissions.');
      return;
    }
    toast.success('Role permissions saved.');
    void loadAll();
  }

  async function createRole(e: FormEvent): Promise<void> {
    e.preventDefault();
    const name = newRoleName.trim();
    if (!name) {
      toast.error('Role name is required.');
      return;
    }
    const response = await authFetch(ADMIN_ROLES_URL, {
      method: 'POST',
      body: JSON.stringify({
        name,
        displayName: newRoleDisplayName.trim() || name,
        permissions: []
      })
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not create role.');
      return;
    }
    toast.success('Role created.');
    setNewRoleName('');
    setNewRoleDisplayName('');
    void loadAll();
  }

  async function deleteSelectedRole(): Promise<void> {
    if (!selectedRoleId) {
      return;
    }
    const response = await authFetch(`${ADMIN_ROLES_URL}/${encodeURIComponent(selectedRoleId)}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const t = await response.text();
      toast.error(t || 'Could not delete role.');
      return;
    }
    toast.success('Role deleted.');
    setSelectedRoleId('');
    setRoleDisplayName('');
    setRolePermissionSet([]);
    void loadAll();
  }

  const catalogByArea = permissionCatalog.reduce<Record<string, ActionEntitlementRow[]>>((acc, row) => {
    const key = row.resourceArea;
    acc[key] = acc[key] ?? [];
    acc[key].push(row);
    return acc;
  }, {});

  return (
    <div className="admin-page">
      <h2>Administration</h2>
      <p className="hint">
        Manage user profiles, region master data, transaction numbering, and system options. New users can sign in with
        password Pass@123 once a profile exists. Set each user&apos;s notification email for workflow alerts; use{' '}
        <strong>System Configuration</strong> to enable email sending and the app link used in messages. SMTP host and
            credentials stay in API appsettings (<span className="mono-id">Smtp</span> section).{' '}
            <strong>Sourcing (RFQ)</strong> controls whether procurement uses the request-for-quote workflow or creates
            purchase orders manually from procurement review. <strong>Material transfer module</strong> controls whether
            the app shows Inventory and Material transfer pages (backend inventory creation from purchase orders still
            runs when this is off).
      </p>

      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          className={tab === 'users' ? 'tab-active' : ''}
          onClick={() => setTab('users')}
        >
          User profiles
        </button>
        <button
          type="button"
          className={tab === 'roles' ? 'tab-active' : ''}
          onClick={() => setTab('roles')}
        >
          Roles
        </button>
        <button
          type="button"
          className={tab === 'regions' ? 'tab-active' : ''}
          onClick={() => setTab('regions')}
        >
          Regions
        </button>
        <button
          type="button"
          className={tab === 'numbering' ? 'tab-active' : ''}
          onClick={() => setTab('numbering')}
        >
          Transaction Configuration
        </button>
        <button
          type="button"
          className={tab === 'materialSku' ? 'tab-active' : ''}
          onClick={() => setTab('materialSku')}
        >
          Material SKU
        </button>
        <button
          type="button"
          className={tab === 'system' ? 'tab-active' : ''}
          onClick={() => setTab('system')}
        >
          System Configuration
        </button>
      </div>

      {loading && <p>Loading…</p>}

      {tab === 'users' && !loading && (
        <div className="admin-section">
          <h3>Directory</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User id</th>
                  <th>Display name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Active</th>
                  <th>Regions</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId}>
                    <td className="mono-id">{u.userId}</td>
                    <td>{u.displayName}</td>
                    <td>{u.email?.trim() || '—'}</td>
                    <td>{u.role}</td>
                    <td>{u.isActive ? 'Yes' : 'No'}</td>
                    <td>{u.allRegions ? 'All' : u.assignedRegionCodes.map(wellRegionLabel).join(', ')}</td>
                    <td>
                      <button type="button" className="secondary" onClick={() => void editUser(u)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>{userForm.userId ? `Edit ${userForm.userId}` : 'Add or update user'}</h3>
          <form className="grid admin-form" onSubmit={(e) => void saveUser(e)}>
            <label>
              User id (login)
              <input
                value={userForm.userId}
                onChange={(e) => setUserForm({ ...userForm, userId: e.target.value })}
                required
                disabled={users.some((x) => x.userId === userForm.userId.trim())}
                autoComplete="off"
              />
            </label>
            <label>
              Display name
              <input
                value={userForm.displayName}
                onChange={(e) => setUserForm({ ...userForm, displayName: e.target.value })}
                required
              />
            </label>
            <label>
              Notification email
              <input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="name@company.com"
                autoComplete="off"
              />
            </label>
            <label>
              Role
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value as UserRole })}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className="full-width admin-region-checkboxes">
              <legend>Application roles (union of permissions)</legend>
              <div className="region-checkbox-grid">
                {(appRoles.length > 0 ? appRoles.map((r) => r.name) : ROLE_OPTIONS).map((name) => (
                  <label key={name} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={userForm.applicationRoles.includes(name)}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setUserForm((prev) => ({
                          ...prev,
                          applicationRoles: on
                            ? [...prev.applicationRoles, name]
                            : prev.applicationRoles.filter((n) => n !== name)
                        }));
                      }}
                    />
                    {name}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={userForm.isActive}
                onChange={(e) => setUserForm({ ...userForm, isActive: e.target.checked })}
              />
              Active (inactive users cannot sign in)
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={userForm.allRegions}
                onChange={(e) => setUserForm({ ...userForm, allRegions: e.target.checked })}
              />
              All regions
            </label>
            {!userForm.allRegions && (
              <fieldset className="full-width admin-region-checkboxes">
                <legend>Assigned regions</legend>
                <div className="region-checkbox-grid">
                  {regions
                    .filter((r) => r.isActive)
                    .map((r) => (
                      <label key={r.code} className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={userForm.selectedRegionCodes.includes(r.code)}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setUserForm((prev) => ({
                              ...prev,
                              selectedRegionCodes: on
                                ? [...prev.selectedRegionCodes, r.code]
                                : prev.selectedRegionCodes.filter((c) => c !== r.code)
                            }));
                          }}
                        />
                        {r.name} <span className="mono-id">({r.code})</span>
                      </label>
                    ))}
                </div>
              </fieldset>
            )}
            <div className="row">
              <button type="submit">Save profile</button>
              <button
                type="button"
                className="secondary"
                onClick={() =>
                  setUserForm({
                    userId: '',
                    displayName: '',
                    email: '',
                    role: 'Requestor',
                    isActive: true,
                    allRegions: true,
                    selectedRegionCodes: [],
                    applicationRoles: []
                  })
                }
              >
                Clear form
              </button>
            </div>
          </form>
        </div>
      )}

      {tab === 'roles' && !loading && (
        <div className="admin-section">
          <h3>Roles</h3>
          <p className="hint">
            Permissions are <span className="mono-id">entity:verb</span> codes. A user with two roles receives the union.
            System roles cannot be deleted.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Display</th>
                  <th>System</th>
                  <th>Permissions</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {appRoles.map((r) => (
                  <tr key={r.id}>
                    <td className="mono-id">{r.name}</td>
                    <td>{r.displayName}</td>
                    <td>{r.isSystem ? 'Yes' : 'No'}</td>
                    <td>{r.permissions.length}</td>
                    <td>
                      <button type="button" className="secondary" onClick={() => selectRole(r)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Create role</h3>
          <form className="grid admin-form" onSubmit={(e) => void createRole(e)}>
            <label>
              Name
              <input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                required
                autoComplete="off"
              />
            </label>
            <label>
              Display name
              <input
                value={newRoleDisplayName}
                onChange={(e) => setNewRoleDisplayName(e.target.value)}
                autoComplete="off"
              />
            </label>
            <div className="row">
              <button type="submit">Create role</button>
            </div>
          </form>

          {selectedRoleId && (
            <>
              <h3>Edit permissions</h3>
              <form className="grid admin-form" onSubmit={(e) => void saveSelectedRole(e)}>
                <label>
                  Display name
                  <input
                    value={roleDisplayName}
                    onChange={(e) => setRoleDisplayName(e.target.value)}
                    required
                  />
                </label>
                {Object.keys(catalogByArea)
                  .sort((a, b) => a.localeCompare(b))
                  .map((area) => (
                    <fieldset key={area} className="full-width admin-region-checkboxes">
                      <legend>{area}</legend>
                      <div className="region-checkbox-grid">
                        {catalogByArea[area].map((ent) => {
                          const code = permissionCode(ent.resourceArea, ent.action);
                          return (
                            <label key={ent.id} className="checkbox-row">
                              <input
                                type="checkbox"
                                checked={rolePermissionSet.some((p) => p.toLowerCase() === code)}
                                onChange={(e) => {
                                  const on = e.target.checked;
                                  setRolePermissionSet((prev) =>
                                    on
                                      ? [...prev, code]
                                      : prev.filter((p) => p.toLowerCase() !== code)
                                  );
                                }}
                              />
                              {code}
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  ))}
                <div className="row">
                  <button type="submit">Save permissions</button>
                  {appRoles.find((r) => r.id === selectedRoleId)?.isSystem ? null : (
                    <button type="button" className="secondary" onClick={() => void deleteSelectedRole()}>
                      Delete role
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      )}

      {tab === 'regions' && !loading && (
        <div className="admin-section">
          <h3>Regions</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Fields</th>
                  <th>Deliveries</th>
                  <th>Active</th>
                  <th>Sort</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => (
                  <tr key={r.id || r.code}>
                    <td className="mono-id">{r.code}</td>
                    <td>{r.name}</td>
                    <td>{r.fields?.length ?? 0}</td>
                    <td>{r.deliveryAddresses?.length ?? 0}</td>
                    <td>{r.isActive ? 'Yes' : 'No'}</td>
                    <td>{r.sortOrder}</td>
                    <td>
                      <button type="button" className="secondary" onClick={() => editRegion(r)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>{regionForm.code ? `Edit ${regionForm.code}` : 'Add region'}</h3>
          <form className="grid admin-form" onSubmit={(e) => void saveRegion(e)}>
            <input type="hidden" value={regionForm.id ?? ''} readOnly />
            <label>
              Code
              <input
                value={regionForm.code}
                onChange={(e) => setRegionForm({ ...regionForm, code: e.target.value })}
                required
              />
            </label>
            <label>
              Name
              <input
                value={regionForm.name}
                onChange={(e) => setRegionForm({ ...regionForm, name: e.target.value })}
                required
              />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={regionForm.isActive}
                onChange={(e) => setRegionForm({ ...regionForm, isActive: e.target.checked })}
              />
              Active
            </label>
            <label>
              Sort order
              <input
                type="number"
                value={regionForm.sortOrder}
                onChange={(e) => setRegionForm({ ...regionForm, sortOrder: Number(e.target.value) || 0 })}
              />
            </label>

            <fieldset className="full-width admin-region-fields">
              <legend>Fields (request form dropdown)</legend>
              <p className="hint">
                Each field needs a name (shown in the dropdown). County, state, closest city, and miles from city are
                shown on request details when that field is selected.
              </p>
              {regionForm.fields.map((f, idx) => (
                <div key={`${f.id || 'new'}-${idx}`} className="admin-region-field-row">
                  <label>
                    Name
                    <input
                      value={f.name}
                      onChange={(e) => {
                        const next = [...regionForm.fields];
                        next[idx] = { ...next[idx], name: e.target.value };
                        setRegionForm({ ...regionForm, fields: next });
                      }}
                      placeholder="Field name"
                    />
                  </label>
                  <label>
                    County
                    <input
                      value={f.county}
                      onChange={(e) => {
                        const next = [...regionForm.fields];
                        next[idx] = { ...next[idx], county: e.target.value };
                        setRegionForm({ ...regionForm, fields: next });
                      }}
                    />
                  </label>
                  <label>
                    State
                    <input
                      value={f.state}
                      onChange={(e) => {
                        const next = [...regionForm.fields];
                        next[idx] = { ...next[idx], state: e.target.value };
                        setRegionForm({ ...regionForm, fields: next });
                      }}
                    />
                  </label>
                  <label>
                    Closest city
                    <input
                      value={f.closestCity}
                      onChange={(e) => {
                        const next = [...regionForm.fields];
                        next[idx] = { ...next[idx], closestCity: e.target.value };
                        setRegionForm({ ...regionForm, fields: next });
                      }}
                      placeholder="e.g. Midland"
                    />
                  </label>
                  <label>
                    Miles from city
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={f.milesFromCity === undefined || f.milesFromCity === null ? '' : String(f.milesFromCity)}
                      onChange={(e) => {
                        const next = [...regionForm.fields];
                        const v = e.target.value.trim();
                        next[idx] = {
                          ...next[idx],
                          milesFromCity: v === '' ? undefined : Number(v)
                        };
                        setRegionForm({ ...regionForm, fields: next });
                      }}
                      placeholder="Optional"
                    />
                  </label>
                  <button
                    type="button"
                    className="secondary danger-text"
                    onClick={() => {
                      const next = regionForm.fields.filter((_, i) => i !== idx);
                      setRegionForm({ ...regionForm, fields: next.length ? next : [emptyRegionFieldRow()] });
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="secondary"
                onClick={() => setRegionForm({ ...regionForm, fields: [...regionForm.fields, emptyRegionFieldRow()] })}
              >
                Add field
              </button>
            </fieldset>

            <fieldset className="full-width admin-region-fields admin-delivery-fieldset">
              <legend>Delivery addresses (this region only)</legend>
              <p className="hint admin-delivery-fieldset-hint">
                Id is optional for new rows (assigned on save). Label and multi-line address are required for each row
                you keep.
              </p>
              <div className="admin-delivery-list">
                {regionForm.deliveryAddresses.map((d, idx) => (
                  <div key={`${d.id || 'new'}-da-${idx}`} className="admin-delivery-card">
                    <div className="admin-delivery-card-head">
                      <span className="admin-delivery-card-title">Location {idx + 1}</span>
                      <button
                        type="button"
                        className="secondary danger-text admin-delivery-remove"
                        onClick={() => {
                          const next = regionForm.deliveryAddresses.filter((_, i) => i !== idx);
                          setRegionForm({
                            ...regionForm,
                            deliveryAddresses: next.length ? next : [emptyRegionDeliveryRow()]
                          });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="admin-delivery-card-body">
                      <label className="admin-delivery-label">
                        Id <span className="admin-delivery-optional">(optional)</span>
                        <input
                          className="admin-delivery-input"
                          value={d.id}
                          onChange={(e) => {
                            const next = [...regionForm.deliveryAddresses];
                            next[idx] = { ...next[idx], id: e.target.value };
                            setRegionForm({ ...regionForm, deliveryAddresses: next });
                          }}
                          placeholder="e.g. da-sok-1"
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </label>
                      <label className="admin-delivery-label">
                        Label
                        <input
                          className="admin-delivery-input"
                          value={d.label}
                          onChange={(e) => {
                            const next = [...regionForm.deliveryAddresses];
                            next[idx] = { ...next[idx], label: e.target.value };
                            setRegionForm({ ...regionForm, deliveryAddresses: next });
                          }}
                          placeholder="Site or warehouse name"
                        />
                      </label>
                      <label className="admin-delivery-label">
                        Site type
                        <select
                          className="admin-delivery-input"
                          value={d.category === 'WellPad' ? 'WellPad' : 'Yard'}
                          onChange={(e) => {
                            const next = [...regionForm.deliveryAddresses];
                            next[idx] = {
                              ...next[idx],
                              category: e.target.value === 'WellPad' ? 'WellPad' : 'Yard'
                            };
                            setRegionForm({ ...regionForm, deliveryAddresses: next });
                          }}
                        >
                          <option value="Yard">Yard</option>
                          <option value="WellPad">Well pad</option>
                        </select>
                      </label>
                      <label className="admin-delivery-label admin-delivery-label--block">
                        Address
                        <textarea
                          className="admin-delivery-textarea"
                          rows={4}
                          value={d.address}
                          onChange={(e) => {
                            const next = [...regionForm.deliveryAddresses];
                            next[idx] = { ...next[idx], address: e.target.value };
                            setRegionForm({ ...regionForm, deliveryAddresses: next });
                          }}
                          placeholder={'Street, city, state, postal code, country — one line or several'}
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="secondary admin-delivery-add-btn"
                onClick={() =>
                  setRegionForm({
                    ...regionForm,
                    deliveryAddresses: [...regionForm.deliveryAddresses, emptyRegionDeliveryRow()]
                  })
                }
              >
                Add delivery address
              </button>
            </fieldset>

            <div className="row">
              <button type="submit">Save region</button>
              <button
                type="button"
                className="secondary"
                onClick={() =>
                  setRegionForm({
                    id: undefined,
                    code: '',
                    name: '',
                    isActive: true,
                    sortOrder: 0,
                    fields: [],
                    deliveryAddresses: []
                  })
                }
              >
                Clear form
              </button>
            </div>
          </form>
        </div>
      )}

      {tab === 'materialSku' && !loading && (
        <div className="admin-section">
          <h3>Material SKU formats</h3>
          <p className="hint">
            One pattern per material type. Use literal text and{' '}
            <span className="mono-id">{'{Placeholders}'}</span> filled from the line (e.g.{' '}
            <span className="mono-id">{'{Diameter}'}</span>, <span className="mono-id">{'{Weight}'}</span>,{' '}
            <span className="mono-id">{'{MfgProcess}'}</span>, <span className="mono-id">{'{Type}'}</span>,{' '}
            <span className="mono-id">{'{Cooling}'}</span>, <span className="mono-id">{'{Output}'}</span>,{' '}
            <span className="mono-id">{'{NHV}'}</span>, etc.). Empty parts become <span className="mono-id">X</span>.
            Procurement can still override the generated SKU on each request line.
          </p>
          <form className="grid admin-form" onSubmit={(e) => void saveMaterialSku(e)}>
            {(Object.keys(DEFAULT_MATERIAL_SKU_PATTERNS) as (keyof typeof DEFAULT_MATERIAL_SKU_PATTERNS)[]).map(
              (kind) => (
                <label key={kind} className="full-width">
                  {kind}
                  <input
                    value={materialSkuForm[kind] ?? ''}
                    onChange={(e) =>
                      setMaterialSkuForm({ ...materialSkuForm, [kind]: e.target.value })
                    }
                    spellCheck={false}
                    autoComplete="off"
                  />
                </label>
              )
            )}
            <div className="row">
              <button type="submit">Save material SKU configuration</button>
            </div>
          </form>
        </div>
      )}

      {tab === 'numbering' && !loading && (
        <div className="admin-section">
          <h3>Transaction Configuration</h3>
          <p className="hint">
            Use placeholders in curly braces. Request IDs may use{' '}
            <span className="mono-id">
              {'{yyyy} {yy} {MM} {dd} {RegionCode} {WellId} {WellNameSlug} {seq:N}'}
            </span>
            . Purchase orders may use{' '}
            <span className="mono-id">{'{Business} {BusinessCode} {MM} {yyyy} {yy} {seq:N}'}</span>. Include exactly one
            sequence token. Until you save a configuration here, the API keeps legacy formats (PR-YYYY-###### and
            PO-######).
          </p>
          <form className="grid admin-form" onSubmit={(e) => void saveNumbering(e)}>
            <label className="full-width">
              Request ID pattern
              <input
                value={numberingForm.requestIdPattern}
                onChange={(e) => setNumberingForm({ ...numberingForm, requestIdPattern: e.target.value })}
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <label className="full-width">
              Purchase order pattern
              <input
                value={numberingForm.purchaseOrderPattern}
                onChange={(e) => setNumberingForm({ ...numberingForm, purchaseOrderPattern: e.target.value })}
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <label>
              Business code (PO tokens)
              <input
                value={numberingForm.businessCode}
                onChange={(e) => setNumberingForm({ ...numberingForm, businessCode: e.target.value })}
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <label>
              Default sequence width (for {'{seq}'})
              <input
                type="number"
                min={1}
                max={8}
                value={numberingForm.defaultSequenceWidth}
                onChange={(e) =>
                  setNumberingForm({
                    ...numberingForm,
                    defaultSequenceWidth: Math.min(8, Math.max(1, Number(e.target.value) || 1))
                  })
                }
              />
            </label>
            <label>
              Max formatted length
              <input
                type="number"
                min={24}
                max={128}
                value={numberingForm.maxFormattedLength}
                onChange={(e) =>
                  setNumberingForm({
                    ...numberingForm,
                    maxFormattedLength: Math.min(128, Math.max(24, Number(e.target.value) || 64))
                  })
                }
              />
            </label>
            <label>
              Request counter scope
              <select
                value={numberingForm.requestSequenceScope}
                onChange={(e) => setNumberingForm({ ...numberingForm, requestSequenceScope: e.target.value })}
              >
                {NUMBERING_SCOPE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              PO counter scope
              <select
                value={numberingForm.purchaseOrderSequenceScope}
                onChange={(e) => setNumberingForm({ ...numberingForm, purchaseOrderSequenceScope: e.target.value })}
              >
                {NUMBERING_SCOPE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <div className="full-width admin-transaction-config-preview">
              <h4>Live preview (sample well / region)</h4>
              <p className="hint">
                Sample: region <span className="mono-id">SOK</span>, well <span className="mono-id">WELL-DEMO-01</span>,{' '}
                sequence <span className="mono-id">42</span>, current UTC date.
              </p>
              <p>
                <strong>Request ID:</strong>{' '}
                <span className="mono-id">
                  {previewNumberingPattern(
                    numberingForm.requestIdPattern,
                    numberingForm.businessCode,
                    numberingForm.defaultSequenceWidth
                  )}
                </span>
              </p>
              <p>
                <strong>PO number:</strong>{' '}
                <span className="mono-id">
                  {previewNumberingPattern(
                    numberingForm.purchaseOrderPattern,
                    numberingForm.businessCode,
                    numberingForm.defaultSequenceWidth
                  )}
                </span>
              </p>
            </div>
            <div className="row">
              <button type="submit">Save Transaction Configuration</button>
            </div>
          </form>
        </div>
      )}

      {tab === 'system' && !loading && (
        <div className="admin-section">
          <h3>System Configuration</h3>
          <p className="hint">
            Email notifications are <strong>off</strong> by default. When enabled, the API sends mail only if SMTP is
            configured. Set the app base URL so emails include a working link to the request (e.g.{' '}
            <span className="mono-id">http://localhost:3000</span>). If left empty, the API falls back to{' '}
            <span className="mono-id">Notifications:FrontendBaseUrl</span> in appsettings when set. Vendor RFQ secure
            links expire after the number of days below (used when invitations are sent).
          </p>
          <form className="grid admin-form" onSubmit={(e) => void saveSystemConfiguration(e)}>
            <label className="checkbox-row full-width">
              <input
                type="checkbox"
                checked={systemForm.sourcingEnabled}
                onChange={(e) => setSystemForm({ ...systemForm, sourcingEnabled: e.target.checked })}
              />
              Enable sourcing (RFQ workflow)
            </label>
            <label className="checkbox-row full-width">
              <input
                type="checkbox"
                checked={systemForm.materialTransfersEnabled}
                onChange={(e) =>
                  setSystemForm({ ...systemForm, materialTransfersEnabled: e.target.checked })
                }
              />
              Enable material transfer module (inventory &amp; transfers)
            </label>
            <label className="checkbox-row full-width">
              <input
                type="checkbox"
                checked={systemForm.emailNotificationsEnabled}
                onChange={(e) =>
                  setSystemForm({ ...systemForm, emailNotificationsEnabled: e.target.checked })
                }
              />
              Enable email notifications
            </label>
            <label className="full-width">
              App base URL for request links in email
              <input
                type="url"
                value={systemForm.emailNotificationFrontendBaseUrl}
                onChange={(e) =>
                  setSystemForm({ ...systemForm, emailNotificationFrontendBaseUrl: e.target.value })
                }
                placeholder="https://your-app.example.com"
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <label className="full-width">
              Vendor quote link expiry (days)
              <input
                type="number"
                min={1}
                max={366}
                value={systemForm.vendorQuoteLinkExpiryDays}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setSystemForm({
                    ...systemForm,
                    vendorQuoteLinkExpiryDays: Number.isFinite(n) ? Math.min(366, Math.max(1, Math.floor(n))) : 5
                  });
                }}
              />
            </label>
            <div className="row">
              <button type="submit">Save system configuration</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
